<template>
    <section class="border-card-section">
        <div class="border-card-section__container">
            <CardTitle v-if="!hideTitle && item.mainTitle" :module="item" />

            <div class="border-card__viewport" @mouseenter="pauseAuto" @mouseleave="resumeAuto"
                @touchstart.passive="pauseAuto" @touchend.passive="resumeAuto">
                <button class="border-card__arrow border-card__arrow--left" @click="scrollPrev" aria-label="Previous">
                    <ChevronLeft />
                </button>

                <div class="border-card__track-wrap">
                    <div ref="trackRef" class="border-card__track" :style="trackStyle">
                        <div v-for="(cell, idx) in renderList" :key="genKey(cell, idx)" class="border-card__card"
                            @click="handleCellClick(cell, idx)">
                            <MediaCard class="border-card__visual" :url="getCellVideo(cell) || getCellImage(cell)"
                                :name="getCellTitle(cell)" video-popup />
                            <div v-if="getCellTitle(cell)" class="border-card__footer">
                                <h4 class="border-card__title">{{ getCellTitle(cell) }}</h4>
                            </div>
                        </div>
                    </div>
                </div>

                <button class="border-card__arrow border-card__arrow--right" @click="scrollNext" aria-label="Next">
                    <ChevronRight />
                </button>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, nextTick, onActivated, onDeactivated } from 'vue'
import type { Module } from '@/api/page_config'
import CardTitle from './CardTitle.vue'
import ChevronRight from '@/assets/icons/chevron-right.svg'
import ChevronLeft from '@/assets/icons/chevron-left.svg'
import { getStrWithRecord, jumpToUrl } from './index.js'

const props = withDefaults(defineProps<{
    item: Module
    hideTitle?: boolean
}>(), { hideTitle: false })

const emit = defineEmits<{
    (e: 'click', item: Module, cell?: unknown): void
}>()

const trackRef = ref<HTMLElement | null>(null)

const rawList = computed(() => props.item.fieldData || [])
const total = computed(() => rawList.value.length)

const renderList = computed(() => {
    const list = rawList.value
    if (list.length <= 1) return list
    return [...list, ...list]
})

function genKey(cell: unknown, idx: number): string {
    const c = cell as Record<string, unknown>
    return `bd-${String(c?.id ?? idx)}-${idx >= total.value ? 'dup' : 'orig'}`
}

function getCellImage(cell: unknown): string {
    return getStrWithRecord(cell, 'image')
}
function getCellVideo(cell: unknown): string {
    return getStrWithRecord(cell, 'video')
}
function getCellTitle(cell: unknown): string {
    return getStrWithRecord(cell, 'title')
}
function getCellUrl(cell: unknown): string {
    return getStrWithRecord(cell, 'url')
}

function handleCellClick(cell: unknown, renderIdx: number): void {
    if (total.value > 1 && renderIdx >= total.value) return
    // 视频项点击卡片主体不跳转（只有播放按钮触发播放），图片项正常跳转
    if (getCellVideo(cell)) return
    emit('click', props.item, cell)
    const url = getCellUrl(cell)
    if (url) jumpToUrl(url)
}

/* ============ 轮播逻辑 ============ */

const AUTO_SPEED = 0.4
const CARD_GAP = 12
const CARD_GAP_DESKTOP = 20

let rafId: number | null = null
let offset = 0
let paused = false
let singleSetWidth = 0
let cardWidth = 0
let isManuallyScrolling = false
let manualTimer: ReturnType<typeof setTimeout> | null = null

function getGap(): number {
    return window.innerWidth >= 768 ? CARD_GAP_DESKTOP : CARD_GAP
}

function measure(): void {
    const el = trackRef.value
    if (!el || !el.firstElementChild) return
    cardWidth = (el.firstElementChild as HTMLElement).offsetWidth
    const gap = getGap()
    singleSetWidth = total.value * (cardWidth + gap)
}

function applyTransform(): void {
    if (!trackRef.value) return
    trackRef.value.style.transform = `translateX(${offset}px)`
}

function checkLoop(): void {
    if (singleSetWidth <= 0) return
    if (Math.abs(offset) >= singleSetWidth) {
        offset += singleSetWidth
        applyTransform()
    }
    if (offset > 0) {
        offset -= singleSetWidth
        applyTransform()
    }
}

function tick(): void {
    if (paused || isManuallyScrolling) {
        rafId = requestAnimationFrame(tick)
        return
    }
    offset -= AUTO_SPEED
    applyTransform()
    checkLoop()
    rafId = requestAnimationFrame(tick)
}

function startAuto(): void {
    if (total.value <= 1) return
    stopAuto()
    measure()
    rafId = requestAnimationFrame(tick)
}

function stopAuto(): void {
    if (rafId !== null) {
        cancelAnimationFrame(rafId)
        rafId = null
    }
}

function pauseAuto(): void {
    paused = true
}
function resumeAuto(): void {
    paused = false
}

function scrollOneCard(direction: 1 | -1): void {
    if (total.value <= 1) return
    isManuallyScrolling = true
    if (manualTimer) clearTimeout(manualTimer)

    measure()
    const step = cardWidth + getGap()
    const targetOffset = offset - direction * step
    const startOffset = offset
    const duration = 400
    const startTime = performance.now()

    const animate = (now: number) => {
        const elapsed = now - startTime
        const progress = Math.min(elapsed / duration, 1)
        const eased = 1 - Math.pow(1 - progress, 3)
        offset = startOffset + (targetOffset - startOffset) * eased
        applyTransform()
        checkLoop()
        if (progress < 1) {
            requestAnimationFrame(animate)
        } else {
            manualTimer = setTimeout(() => {
                isManuallyScrolling = false
            }, 100)
        }
    }
    requestAnimationFrame(animate)
}

function scrollNext(): void {
    scrollOneCard(1)
}
function scrollPrev(): void {
    scrollOneCard(-1)
}

const trackStyle = computed(() => ({
    willChange: 'transform'
}))

let resizeObserver: ResizeObserver | null = null

onMounted(() => {
    nextTick(() => {
        measure()
        startAuto()
        if (trackRef.value) {
            resizeObserver = new ResizeObserver(() => {
                measure()
            })
            resizeObserver.observe(trackRef.value)
        }
    })
})

onUnmounted(() => {
    stopAuto()
    if (manualTimer) clearTimeout(manualTimer)
    if (resizeObserver) resizeObserver.disconnect()
})

onActivated(() => {
    resumeAuto()
})
onDeactivated(() => {
    pauseAuto()
})
</script>

<style scoped lang="scss">
@use '@/assets/styles/variables' as *;

.border-card-section {
    width: 100%;
}

.border-card-section__container {
    max-width: $container-max-width;
    margin: 0 auto;
    padding: 0 1rem;

    @media (min-width: $breakpoint-md) {
        padding: 0 2rem;
    }
}

.border-card__viewport {
    position: relative;
    padding: 0;
    overflow: hidden;
}

.border-card__track-wrap {
    overflow: hidden;

    @media (min-width: $breakpoint-md) {
        margin: 0;
    }
}

.border-card__track {
    display: flex;
    gap: 0.75rem;
    padding: 0.5rem 0;
    width: max-content;

    @media (min-width: $breakpoint-md) {
        gap: 1.25rem;
    }
}

.border-card__card {
    flex-shrink: 0;
    width: 260px;
    border-radius: 1rem;
    overflow: hidden;
    cursor: pointer;
    position: relative;
    background: #fff;
    border: 1px solid #e5e5e5;
    transition: transform 0.3s ease, box-shadow 0.3s ease;

    @media (min-width: $breakpoint-md) {
        width: 380px;
        border-radius: 1.25rem;
    }

    &:hover {
        transform: translateY(-4px);
        box-shadow: 0 12px 28px rgba(0, 0, 0, 0.1);

        .border-card__visual img,
        .border-card__visual video {
            transform: scale(1.04);
        }
    }
}

.border-card__visual {
    position: relative;
    width: 100%;
    aspect-ratio: 16 / 10;
    overflow: hidden;
    background: $color-bg-tertiary;

    img,
    video {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
        transition: transform 0.5s ease;
    }
}

.border-card__play-btn {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: #fff;
    border: none;
    color: #ff6d00;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
    transition: transform 0.25s ease, box-shadow 0.25s ease;

    svg {
        width: 22px;
        height: 22px;
        margin-left: 2px;
    }

    @media (min-width: $breakpoint-md) {
        width: 64px;
        height: 64px;

        svg {
            width: 26px;
            height: 26px;
        }
    }

    &:hover {
        transform: translate(-50%, -50%) scale(1.1);
        box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
    }
}

.border-card__footer {
    padding: 1rem 1.25rem;
    background: #fff;
}

.border-card__title {
    font-size: 1rem;
    font-weight: 700;
    color: $color-text;
    margin: 0;
    line-height: 1.4;

    @media (min-width: $breakpoint-md) {
        font-size: 1.125rem;
    }
}

/* 左右箭头按钮 */
.border-card__arrow {
    position: absolute;
    top: 50%;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: none;
    cursor: pointer;
    z-index: 3;
    color: #555;
    transition: all 0.2s ease;
    transform: translateY(-50%);

    @media (max-width: 767px) {
        display: none;
    }

    @media (min-width: $breakpoint-md) {
        width: 48px;
        height: 48px;
    }

    svg {
        width: 32px;
        height: 32px;
        stroke-width: 2;
    }

    &:hover {
        color: #000;
        transform: translateY(-50%) scale(1.15);
    }

    &--left {
        left: -0.25rem;

        @media (min-width: $breakpoint-md) {
            left: -0.5rem;
        }
    }

    &--right {
        right: -0.25rem;

        @media (min-width: $breakpoint-md) {
            right: -0.5rem;
        }
    }
}
</style>
